/**
 * Shared UI component helpers — reusable HTML generators for bars, badges, chips.
 * Eliminates duplication across runners.ts, runner-sidebar.ts, contacts.ts, etc.
 */

import type { RunnerDTO } from '../data/fixture';
import { t } from '../i18n';
import { attrColor } from './colors';

// ── Status Badge ────────────────────────────────────────────────────────────

/** Render a colored status badge for a runner. */
export function statusBadge(r: RunnerDTO): string {
  let label: string;
  let color: string;

  if (r.status === 'arrested') {
    label = (r.arrestedUntil != null && r.arrestedUntil > 0)
      ? t('runners.status_arrested_until', { day: String(r.arrestedUntil) })
      : t('runners.status_arrested');
    color = 'var(--red)';
  } else if (r.status === 'injured' || (r.status === 'active' && r.hp < 30)) {
    label = t('runners.status_injured');
    color = 'var(--amber)';
  } else if (r.status === 'manager') {
    label = t('runners.status_manager');
    color = 'var(--cyan)';
  } else if (r.status === 'active' && r.heat === 0 && r.stress === 0) {
    label = t('runners.status_idle');
    color = 'var(--text-lo)';
  } else {
    label = t('runners.status_active');
    color = 'var(--green)';
  }

  return `<span class="status-badge" style="font-family:var(--mono);font-size:11px;padding:2px 6px;border-radius:3px;background:${color}22;border:1px solid ${color}55;color:${color}">${label}</span>`;
}

// ── Skill Chips ─────────────────────────────────────────────────────────────

/** Render inline skill chips (colored tags). */
export function skillChips(skills: string[]): string {
  if (!skills || skills.length === 0) return '<span style="color:var(--text-lo)">—</span>';
  return skills.map(s =>
    `<span style="display:inline-block;padding:1px 5px;margin:0 2px;border-radius:3px;background:var(--bg-deep);color:var(--cyan);font-size:10px;font-family:var(--mono)">${s}</span>`,
  ).join('');
}

// ── Dials (radial donut gauges) ──────────────────────────────────────────────

/**
 * Large radial donut gauge tile for the KPI hero row.
 * `colorVar` is a token name (e.g. 'amber', 'cyan', 'green', 'red'); the
 * conic-gradient uses the CSS var so the dial re-themes with --ac.
 */
export function dialTile(value: number, colorVar: string, caption: string, numText: string): string {
  const v = Math.max(0, Math.min(100, value));
  const col = `var(--${colorVar})`;
  return `<div class="dial-tile">
    <div class="dial lg">
      <div class="ring" style="background:conic-gradient(${col} ${v}%, rgba(255,255,255,0.07) 0);filter:drop-shadow(0 0 5px ${col})"></div>
      <div class="num" style="color:${col}">${numText}</div>
      <span class="cap">${caption}</span>
    </div>
  </div>`;
}

/** Compact donut gauge (topbar). No caption; smaller ring. */
export function dialMini(value: number, colorVar: string, numText: string): string {
  const v = Math.max(0, Math.min(100, value));
  const col = `var(--${colorVar})`;
  return `<div class="dial sm" title="${caption2(numText)}">
    <div class="ring" style="background:conic-gradient(${col} ${v}%, rgba(255,255,255,0.07) 0)"></div>
    <div class="num" style="color:${col}">${numText}</div>
  </div>`;
}

function caption2(s: string): string { return s.replace(/"/g, '&quot;'); }

/**
 * Compact cash for KPI hero tiles: $189.22M, $5.2K, $840. Keeps large values
 * inside the narrow angled cards (full formatCash would overflow/clip).
 * Sign-aware (− prefix for negatives); callers add an explicit + where wanted.
 */
export function formatCashCompact(n: number): string {
  const sign = n < 0 ? '−' : '';
  const a = Math.abs(n);
  let s: string;
  if (a >= 1e9)      s = (a / 1e9).toFixed(2) + 'B';
  else if (a >= 1e6) s = (a / 1e6).toFixed(2) + 'M';
  else if (a >= 1e3) s = (a / 1e3).toFixed(1) + 'K';
  else               s = String(Math.round(a));
  return `${sign}$${s}`;
}

// ── Bars ─────────────────────────────────────────────────────────────────────

/** Tiny inline bar (roster table cells). */
export function miniBar(val: number, color: string): string {
  return `<div style="height:4px;background:var(--border);border-radius:2px;overflow:hidden;width:50px;display:inline-block;vertical-align:middle">
    <div style="height:100%;width:${val}%;background:${color};border-radius:2px"></div>
  </div>`;
}

/** Attribute mini-bar with value label (table cells). */
export function attrMiniBar(val: number): string {
  const pct = Math.min(100, Math.max(0, val));
  const color = attrColor(val);
  return `<div style="display:inline-flex;align-items:center;gap:4px">
    <div style="height:4px;background:var(--border);border-radius:2px;overflow:hidden;width:36px;display:inline-block;vertical-align:middle">
      <div style="height:100%;width:${pct}%;background:${color};border-radius:2px"></div>
    </div>
    <span style="font-family:var(--mono);font-size:11px;color:${color};min-width:16px;text-align:right">${val}</span>
  </div>`;
}

/** Full-width attribute bar with label (detail panels, CSS-class based). */
export function attrBar(label: string, val: number): string {
  const pct = Math.min(100, Math.max(0, val));
  const color = attrColor(val);
  return `<div class="detail-attr-row">
    <span class="detail-attr-label">${label}</span>
    <div class="detail-attr-track"><div class="detail-attr-fill" style="width:${pct}%;background:${color}"></div></div>
    <span class="detail-attr-val">${val}</span>
  </div>`;
}

/** Full-width state bar with label and optional warning icon (detail panels). */
export function stateBar(label: string, val: number, color: string, warn: boolean): string {
  return `<div class="detail-state-row">
    <span class="detail-state-label">${label}</span>
    <div class="detail-attr-track"><div class="detail-attr-fill" style="width:${val}%;background:${color}"></div></div>
    <span class="detail-state-val" style="color:${color}">${val}</span>
    ${warn ? '<span class="detail-state-warn">⚠</span>' : ''}
  </div>`;
}

/** Inline attribute bar with label (sidebar style — inline CSS). */
export function inlineAttrBar(label: string, val: number): string {
  const pct = Math.min(100, Math.max(0, val));
  const color = attrColor(val);
  return `<div style="display:flex;align-items:center;gap:6px;margin-bottom:4px">
    <span style="font-family:var(--mono);font-size:12px;color:var(--text-lo);width:26px;text-transform:uppercase;letter-spacing:1px">${label}</span>
    <div style="flex:1;height:5px;background:var(--border);border-radius:2px;overflow:hidden"><div style="height:100%;width:${pct}%;background:${color};border-radius:2px"></div></div>
    <span style="font-family:var(--mono);font-size:11px;color:var(--text-mid);width:22px;text-align:right">${val}</span>
  </div>`;
}

/** Inline state bar with label (sidebar style — inline CSS). */
export function inlineStateBar(label: string, val: number, color: string, warn: boolean): string {
  return `<div style="display:flex;align-items:center;gap:6px;margin-bottom:4px">
    <span style="font-family:var(--mono);font-size:12px;color:var(--text-lo);width:34px;text-transform:uppercase;letter-spacing:1px">${label}</span>
    <div style="flex:1;height:5px;background:var(--border);border-radius:2px;overflow:hidden"><div style="height:100%;width:${val}%;background:${color};border-radius:2px"></div></div>
    <span style="font-family:var(--mono);font-size:11px;width:22px;text-align:right;color:${color}">${val}</span>
    ${warn ? '<span style="color:var(--red);font-size:11px">⚠</span>' : ''}
  </div>`;
}
